// ─────────────────────────────────────────────
//  cardMessage — the room card's single unified message slot.
//  Consolidates the passive status strips (break window countdown, giving a
//  break, relief pending, next-room routing, incoming team, room comment)
//  into ONE message: highest-priority event wins, everything else waits its
//  turn. Interactive bars (break request/cover, coverage ack, alerts) are
//  controls, not messages — they stay out of this slot.
// ─────────────────────────────────────────────
import { nextRoomLink, isStagedArrival } from "./nextRoom";
import { isNameCandidate, nameWordsOf } from "./providerCell";
import { isSoloCase } from "./timeline";
import { isBeingRelieved, isCoverPending } from "./breaks";

export const MESSAGE_TONES = {
  red: "red", // overdue break window
  amber: "amber", // break / relief activity
  navy: "navy", // outgoing next-room link
  green: "green", // incoming team
  muted: "muted", // room comment
};

const chLabel = (ch) => (ch ? ch.charAt(0).toUpperCase() + ch.slice(1) : "break");

// Human wording for a coverage gap (OB 24h CRNA away covering another room).
const coverageReason = (c) =>
  c.reason === "break" ? "CRNA on break"
  : c.reason === "away" ? "CRNA stepped away"
  : `CRNA covering ${c.awayRoomName || "another room"}`;

// Returns { kind, text, tone, blink } or null when the card has nothing to say.
// Priority (most urgent first):
//   1. EMERGENCY in this room
//   2. coverage gap (room not covered)
//   3. URGENT (private, doctor/CRNA only)
//   4. supervising MD tied up in a solo case elsewhere (Reassign MD action)
//   5. approved break window running (countdown; red when overdue)
//   6. a live break REQUEST waiting for coverage
//   7. someone is giving a break in this room
//   8. staged relief pending takeover
//   9. this team's next room
//  10. a team incoming from another room
//  11. the room comment
// Same-doctor SOLO chain: when one doctor runs two (or more) solo rooms
// today, link the cards — the earlier case gets { dir:"next" } pointing at the
// later room, the later case gets { dir:"coming" } pointing back. Solo-only:
// a supervisor covering several rooms is normal and must not chain.
export function mdSoloChain(assignment, allAssignments = []) {
  const a = assignment;
  if (!a || !isSoloCase(a) || !a.attendingId || !a.orRoom) return null;
  const mins = (t) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || "").trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const myStart = mins(a.startTime);
  if (myStart == null) return null;
  const others = (allAssignments || [])
    .filter((o) => o && o.id !== a.id && isSoloCase(o) && o.attendingId === a.attendingId && o.orRoom && o.orRoom !== a.orRoom)
    .map((o) => ({ o, s: mins(o.startTime) }))
    .filter((x) => x.s != null);
  if (!others.length) return null;
  const next = others.filter((x) => x.s > myStart).sort((x, y) => x.s - y.s)[0];
  if (next) return { dir: "next", room: next.o.orRoom, time: next.o.startTime || "" };
  const prev = others.filter((x) => x.s < myStart).sort((x, y) => y.s - x.s)[0];
  if (prev) return { dir: "coming", room: prev.o.orRoom, time: prev.o.startTime || "" };
  return null;
}

export function buildCardMessage({
  room = {}, assignment = null, incomingTeams = [], countdown = null,
  breakRequest = null, emergency = null, urgency = null, coverage = null,
  mdUnavailable = null, removedSupervisor = null, allAssignments = [],
}) {
  if (emergency) {
    const acks = emergency.ackNames || [];
    return {
      kind: "emergency",
      text: `🚨 EMERGENCY${emergency.senderName ? ` — ${emergency.senderName}` : ""}${emergency.since ? ` · ${emergency.since}` : ""}${acks.length ? ` · ✓ ${acks.join(", ")}` : ""}`,
      tone: MESSAGE_TONES.red,
      blink: true,
    };
  }
  if (coverage && coverage.uncovered) {
    return {
      kind: "coverage",
      text: `🚨 Not covered — ${coverageReason(coverage)}${coverage.acked ? ` · ✓ ${coverage.ackByName || "acknowledged"}` : ""}`,
      tone: MESSAGE_TONES.red,
      blink: !coverage.acked,
    };
  }
  if (urgency) {
    return {
      kind: "urgency",
      text: `⚠ URGENT${urgency.senderName ? ` — ${urgency.senderName}` : ""}${urgency.since ? ` · ${urgency.since}` : ""}`,
      tone: MESSAGE_TONES.amber,
      blink: true,
    };
  }
  if (removedSupervisor) {
    // The room's supervising MD was removed from MD Call (TV X button) and is
    // pending Home confirmation — every room they supervise flags it until a
    // new MD is reassigned or the removal is cancelled.
    return {
      kind: "supervisorRemoved",
      text: `⚠ Supervisor not available${removedSupervisor.name ? ` — ${removedSupervisor.name}` : ""}`,
      tone: MESSAGE_TONES.red,
      blink: true,
    };
  }
  if (mdUnavailable) {
    // Their supervising MD is running a solo case in another room. Before it
    // starts: warn with the start time. Once that room is ACTIVE: warn live.
    return {
      kind: "mdUnavailable",
      text: mdUnavailable.active
        ? `⚠ MD unavailable — solo in ${mdUnavailable.roomName}`
        : `⚠ MD unavailable at ${mdUnavailable.startTime || "solo case"}`,
      tone: MESSAGE_TONES.amber,
      blink: !!mdUnavailable.active,
    };
  }
  if (countdown) {
    return {
      kind: "breakWindow",
      text: `☕ ${chLabel(room.breakChannel)}${room.relieverName ? ` · ${room.relieverName}` : ""} · ${countdown.label}`,
      tone: countdown.overdue ? MESSAGE_TONES.red : MESSAGE_TONES.amber,
      blink: !!countdown.overdue,
    };
  }
  if (breakRequest) {
    return {
      kind: "breakRequest",
      text: `☕ ${breakRequest.breakType ? chLabel(breakRequest.breakType) : "Break"} needed${breakRequest.giverName ? ` — ${breakRequest.giverName}` : ""}`,
      tone: MESSAGE_TONES.amber,
      blink: true,
    };
  }
  // ⚠️ GATED ON THE STATE, NOT ON THE FIELD. `relieverName` is written the
  // moment a cover is NAMED, so an ungated read says "Giving a break: X" about
  // a room X has not walked into. Amber is the board's word for "a reliever is
  // standing in this room" — it has to stay true.
  if (room.relieverName && isBeingRelieved(room.breaks || {})) {
    return {
      kind: "breakCover",
      text: `☕ Giving a break: ${room.relieverName}`,
      tone: MESSAGE_TONES.amber,
      blink: false,
    };
  }
  if (room.relieverName && isCoverPending(room.breaks || {})) {
    return {
      kind: "breakCoverPending",
      text: `⏳ ${room.relieverName} on the way`,
      // Navy, never amber: amber is this board's word for a reliever who is
      // already in the room.
      tone: MESSAGE_TONES.navy,
      blink: false,
    };
  }
  const reliefCrna = assignment?.crnaReliefName || "";
  const reliefMd = assignment?.mdReliefName || "";
  if (reliefCrna || reliefMd) {
    return {
      kind: "reliefPending",
      text: `Relief ${reliefCrna || reliefMd}${reliefCrna && reliefMd ? ` · MD ${reliefMd}` : ""} — pending`,
      tone: MESSAGE_TONES.amber,
      blink: true,
    };
  }
  // Same DOCTOR running back-to-back SOLO rooms: link the two cards like the
  // CRNA chains — the earlier case points forward ("Next Room"), the later
  // one points back ("Coming from") — so the board reads the doctor's path.
  const chain = mdSoloChain(assignment, allAssignments);
  if (chain) {
    return {
      kind: "mdChain",
      text: chain.dir === "next"
        ? `➜ Next Room: ${chain.room} · ${chain.time}`
        : `⬅ Coming from: ${chain.room}`,
      tone: MESSAGE_TONES.navy,
      blink: false,
    };
  }
  const next = nextRoomLink(assignment);
  if (next) {
    return {
      kind: "nextRoom",
      text: `Next Room: ${next.roomName}`,
      tone: MESSAGE_TONES.navy,
      blink: false,
    };
  }
  if (incomingTeams.length > 0) {
    // Staged arrival — this card's own row already holds the inbound person
    // (a multi-room CRNA's later case, or a team routed to an empty room):
    // announce where they come from. Scan every inbound row — another room
    // may route here too.
    const staged = incomingTeams.find((t) => isStagedArrival(assignment, t));
    const first = staged || incomingTeams[0];
    const more = incomingTeams.length - 1;
    return {
      kind: "incoming",
      text: staged
        ? `Coming from: ${first.fromRoomName}${more > 0 ? ` (+${more} more)` : ""}`
        : `Incoming from ${first.fromRoomName}${first.label ? ` — ${first.label}` : ""}${more > 0 ? ` (+${more} more)` : ""}`,
      tone: MESSAGE_TONES.green,
      blink: false,
    };
  }
  if (room.comment) {
    return {
      kind: "comment",
      text: `💬 ${room.comment}`,
      tone: MESSAGE_TONES.muted,
      blink: false,
    };
  }
  return null;
}

// Shorten a long name to initials + surname, for a card that cannot fit it.
//
// MOVED HERE FROM components/RoomCard.js. It is a pure string function and by
// the house rule belongs in src/domain/ -- but the reason it moved is concrete:
// the only test covering it did `await import("../components/RoomCard")`, which
// drags a heavy React component (Firebase, contexts, the whole card) into a
// 5-second test to reach five string assertions. Under parallel load that import
// exceeded the timeout and the test failed intermittently, in a file that had
// nothing to do with whatever change was being made -- the worst kind of red.
// RoomCard re-exports this, so every existing call site is untouched.
//
// ⚠️ THE LAST WORD IS NOT ALWAYS THE SURNAME, AND THIS SHORTENS TO THE LAST
// WORD. A card name arrives with the daily sheet's bookkeeping still on it —
// "Charlotte Sanchez 7a-7a" is 23 characters, so it shortened, and it shortened
// to **"C.S. 7a-7a"**: a shift window in the surname slot, initials made out of
// the person's real name. `domain/providerCell.nameWordsOf` owns which trailing
// words are annotations (the same peel `caseBarLabel` uses for the Timeline
// bar); a second copy of that vocabulary here is the drift this repo keeps
// paying for. A name that is ONLY annotations is returned untouched — a blank
// where a name belongs is worse than an odd one.
export const smartName = (name, max = 20) => {
  const s = String(name || "").trim();
  if (s.length <= max) return s;
  // Parenthesized nicknames ("Babajide (Michael) Osunsanmi") don't initialize.
  const parts = s.replace(/\([^)]*\)/g, " ").split(/\s+/).filter(Boolean);
  if (parts.length < 2 || !parts.some(isNameCandidate)) return s;
  const words = nameWordsOf(parts.join(" "));
  if (words.length < 2) return words[0] || s;
  const last = words[words.length - 1];
  const initials = words.slice(0, -1).map((p) => `${p[0].toUpperCase()}.`).join("");
  return `${initials} ${last}`;
};
